import React, { useCallback, useEffect, useState } from 'react';
import useToastContext from 'hooks/useToastContext';
import { apiGet, apiPost } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import PayrollOnboardingWizard from './PayrollOnboardingWizard';
import PaymentLanding from './components/PaymentLanding';
import SubmissionStatus from './components/SubmissionStatus';

/**
 * Entry point for the company "Payroll" page. Route this component instead of the wizard.
 *
 *   not paid          -> PaymentLanding  ($ setup fee, Stripe Checkout)
 *   paid, no form yet -> PayrollOnboardingWizard
 *   form submitted    -> SubmissionStatus
 *
 * Stripe sends the user back to  <COMPANY_REACT_APP_BASE_URL><PAYROLL_RETURN_PATH>?payment=success&session_id=...
 * so this page must be the one mounted at that path (default "/payroll").
 */
const PayrollOnboardingPage = () => {
  const notification = useToastContext();
  const [state, setState] = useState({ loading: true });
  const [paying, setPaying] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiGet(apiPath.payrollStatus);
      if (res?.data?.success) setState({ loading: false, ...res.data.results });
      else {
        setState({ loading: false, error: true });
        notification.error(res?.data?.message || 'Could not load payroll status');
      }
    } catch (e) {
      setState({ loading: false, error: true });
      notification.error('Could not load payroll status');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle the redirect back from Stripe, then load the current state
  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const result = params.get('payment');

      if (result === 'cancelled') setCancelled(true);
      if (sessionId) {
        try {
          const res = await apiPost(apiPath.payrollConfirmPayment, { sessionId });
          if (res?.data?.success) notification.success('Payment received – you can now complete the forms.');
          else notification.error(res?.data?.message || 'We could not confirm your payment yet.');
        } catch (e) {
          notification.error('We could not confirm your payment yet.');
        }
      }
      if (sessionId || result) window.history.replaceState({}, '', window.location.pathname); // clean the URL
      load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pay = async () => {
    try {
      setPaying(true);
      const res = await apiPost(apiPath.payrollCheckout, {});
      const url = res?.data?.results?.sessionUrl;
      if (res?.data?.success && url) {
        window.location.href = url; // Stripe Checkout
        return;
      }
      notification.error(res?.data?.message || 'Could not start the payment.');
    } catch (e) {
      notification.error('Could not start the payment.');
    }
    setPaying(false);
  };

  const { loading, error, fee, payment, onboarding } = state;

  if (loading) return <p className="py-24 text-center text-gray-400">Loading…</p>;
  if (error) return <p className="py-24 text-center text-gray-500">Something went wrong. Please refresh the page.</p>;

  const submitted = onboarding && onboarding.status !== 'Rejected';
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 px-3 py-6 md:py-10">
        <SubmissionStatus onboarding={onboarding} payment={payment} />
      </div>
    );
  }

  if (!payment?.readyForForm) {
    return (
      <div className="min-h-screen bg-gray-100 px-3 py-6 md:py-10">
        <PaymentLanding fee={fee} busy={paying} onPay={pay} cancelled={cancelled} />
      </div>
    );
  }

  return (
    <>
      {onboarding?.status === 'Rejected' && (
        <div className="mx-auto max-w-5xl px-3 pt-6">
          <p className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Your previous submission was not accepted{onboarding.rejection?.reason ? `: ${onboarding.rejection.reason}` : '.'} Your setup
            fee is still on file – please correct the form and submit again.
          </p>
        </div>
      )}
      <PayrollOnboardingWizard />
    </>
  );
};

export default PayrollOnboardingPage;